import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MuiSidebar from '../../components/MuiSidebar';
import { styled, alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Grid,
  Select,
  MenuItem,
  useMediaQuery,
  Card,
  Chip,
  Stack,
} from '@mui/material';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';

import axios from 'axios';
import SessionExpiredPopup from '../../components/SessionExpiredPopup/SessionExpiredPopup';
import useSessionExpired from '../../hooks/useSessionExpired';
import ProjectsSkeleton from '../../components/ProjectsSkeleton';
import AnimatedBackground from '../../components/AnimatedBackground';

/* ------------------------ Styled Components ------------------------ */

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  minHeight: '100vh',
  padding: theme.spacing(3),
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  position: 'relative',
  zIndex: 1,
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  backdropFilter: 'blur(10px)',
  [theme.breakpoints.up('md')]: {
    width: '100%',
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(8),
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(8),
  },
}));

const StatsGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  }
}));

const StatCard = styled(Card)(({ theme, accentcolor }) => ({
  height: '100%',
  width: '100%',
  position: 'relative',
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: accentcolor || theme.palette.primary.main,
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[10],
    '& .MuiTypography-h4': {
      color: accentcolor
    }
  },
  '& .MuiTypography-h6': {
    fontSize: '1.1rem',
    fontWeight: 500,
    color: theme.palette.text.secondary
  },
  '& .MuiTypography-h4': {
    fontSize: '2.2rem',
    fontWeight: 700,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: 'color 0.3s ease'
  }
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  '& .pagination': {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.shape.borderRadius,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
  },
  '& .page-nav, & .page-number': {
    minWidth: '40px',
    height: '40px',
    padding: '0 12px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRadius: theme.shape.borderRadius,
    background: 'transparent',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover:not(:disabled)': {
      background: alpha(theme.palette.primary.main, 0.1),
      borderColor: theme.palette.primary.main,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  '& .page-number.active': {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.main,
  },
  '& .page-info': {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  }
}));

const ProjectsGrid = styled(Grid)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(6),
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  '& .MuiGrid-item': {
    width: '100%',
    padding: '0 !important',
  }
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  }
}));

const ProjectImageSkeleton = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  background: `linear-gradient(90deg, 
    ${alpha(theme.palette.background.paper, 0.6)}, 
    ${alpha(theme.palette.background.paper, 0.8)}, 
    ${alpha(theme.palette.background.paper, 0.6)})`,
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite linear',
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  }
}));

const ProjectContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  '& .project-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  '& .project-id': {
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  '& .project-title': {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
    letterSpacing: '-0.3px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
  },
  '& .project-info-highlight': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '& .project-location': {
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: '0.875rem',
      whiteSpace: 'nowrap',
      backgroundColor: alpha(theme.palette.info.main, 0.1),
      color: theme.palette.info.main
    },
    '& .project-property': {
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: '0.875rem',
      whiteSpace: 'nowrap',
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main
    },
    '& .project-stage': {
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: '0.875rem',
      whiteSpace: 'nowrap',
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      color: theme.palette.success.main
    }
  },
  '& .info-row': {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '&:last-child': { marginBottom: 0 },
    '& > div': { flex: 1 },
    '& .info-label': {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.5),
    },
    '& .info-value': {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
    }
  },
  '& .project-targets': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    '& .target-label': {
      fontSize: '0.75rem',
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
      color: theme.palette.warning.main,
      padding: '4px 12px',
      borderRadius: '16px',
      whiteSpace: 'nowrap',
    }
  }
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    minWidth: '150px',
    flex: 1,
    borderRadius: theme.shape.borderRadius,
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '& > *': {
      width: '100%',
      flex: '1 1 100%',
    }
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  '& .MuiSelect-select': {
    padding: '12px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 14px',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
}));

const SearchInput = styled('input')(({ theme }) => ({
  flex: 1,
  minWidth: '250px',
  padding: '12px 16px',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  fontSize: '1rem',
  color: theme.palette.text.primary,
  transition: 'all 0.3s ease',
  '&::placeholder': {
    color: theme.palette.text.secondary,
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    padding: '10px 14px',
    fontSize: '0.9rem',
  },
}));

/* ------------------------ Helpers ------------------------ */

const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  const n = Number(value);
  if (Number.isNaN(n)) return '0';
  // Format with 2 decimal places for non-integers
  if (n % 1 !== 0) {
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return n.toLocaleString();
};

const formatDate = (dateLike) => {
  if (!dateLike) return 'N/A';
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// We don't seem to need this one anymore as the API provides direct URLs
// const convertGoogleDriveUrl = (url) => {
//   if (!url || !url.includes('drive.google.com')) return url;
//   const fileId = url.match(/id=([^&]+)/)?.[1];
//   if (!fileId) return url;
//   return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
// };

/* ------------------------ Component ------------------------ */

const InvestmentProjects = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const navigate = useNavigate();
  const [originalProjects, setOriginalProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // UI filters/sort
  const [sortBy, setSortBy] = useState('Entry Date (Newest First)');
  const [statusFilter, setStatusFilter] = useState('On-Going'); // UI value
  const [investmentTypeFilter, setInvestmentTypeFilter] = useState('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');

  // Summary
  const [ongoingProjectsCount, setOngoingProjectsCount] = useState(0);
  const [assetValue, setAssetValue] = useState(0);

  // Loading / errors
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const projectsPerPage = 10;
  // This will be updated after filtering
  // const totalProjects = originalProjects.length; 

  const { isSessionExpired, handleSessionExpired, closeSessionExpired } = useSessionExpired();

  const getValidToken = useCallback(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      handleSessionExpired();
      return null;
    }
    return token;
  }, [handleSessionExpired]);

  // Fetch from API you provided: data = Array of projects, pagination meta separated
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = getValidToken();
        if (!token) return;

        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const url = `${base}/projects`;

        const response = await axios.get(url);

        // --- UPDATED ---
        // Shape according to sample you posted
        // { success, message, data: [ ...projects ], asset_value: 123, pagination: {...} }
        const apiList = Array.isArray(response?.data?.data) ? response.data.data : [];
        const apiAssetValue = response?.data?.asset_value ?? 0; // Get root asset_value
        // --- END UPDATE ---

        // Transform each project item to UI model
        const transformed = apiList.map((p) => {
          // Choose entry date as reservation_date (fallback to contract_date)
          const entry = p.reservation_date || p.contract_date || null;

          // Use total_contract_value for "Contract Value" as labeled on the card
          // Use asset_evaluation for a potential "Asset Value" sort/filter
          const contractValueNum = Number(p.total_contract_value ?? 0);
          const assetEvaluationNum = Number(p.asset_evaluation ?? 0);
          const profitNum = 0; // API does not provide profit

          // Normalize labels for display
          const displayStatus = (p.status || '').toLowerCase().replace(/^\w/, c => c.toUpperCase()); // on-going -> On-going
          const displayPropType = (p.type || '').toString().replace(/^\w/, c => c.toUpperCase());
          const displayInvestType = (p.investment_type || '').split('_').map(s => s.replace(/^\w/, c => c.toUpperCase())).join(' ');

          return {
            id: p.id,                                     // uuid
            // --- UPDATED ---
            key: p.key || '',                             // Use 'key' field
            image: (p.images && p.images.length > 0) ? p.images[0].url : null, // Get first image
            // --- END UPDATE ---
            title: p.title || 'Untitled Project',
            entryDate: entry ? new Date(entry) : null,
            exitDate: p.exit_date ? new Date(p.exit_date) : null, // Use exit_date from API

            investedAmount: contractValueNum, // Used for "Contract Value" label
            assetEvaluation: assetEvaluationNum, // Store for sorting
            profit: profitNum,

            investedAmountDisplay: formatNumber(contractValueNum),
            profitDisplay: formatNumber(profitNum),
            roi: contractValueNum > 0 ? ((profitNum / contractValueNum) * 100).toFixed(2) + '%' : '0%',

            // Badges
            status: displayStatus,                      // e.g., "On-going"
            currentStage: p.stage || p.status || '',    // e.g., "holding"
            investmentType: displayInvestType || 'N/A', // nice label
            propertyType: displayPropType || 'N/A',
            location: p.compound?.name || p.location || 'N/A', // Use compound, fallback to location

            // Extra fields from API
            area: p.area,
            gardenArea: p.garden_area,
            contractDate: p.contract_date,
            reservationDate: p.reservation_date,
            totalContractValue: contractValueNum,
            compound: p.compound,
            typeRaw: p.type,
            investmentTypeRaw: p.investment_type,
            locationRaw: p.location, // Store original location
          };
        });

        // Compute summary from transformed list
        const ongoingCount = transformed.filter(pr => pr.status.toLowerCase() === 'on-going').length;
        // const totalAsset = transformed.reduce((sum, pr) => sum + (Number(pr.investedAmount) || 0), 0); // Old way

        setOriginalProjects(transformed);
        setFilteredProjects(transformed);
        setOngoingProjectsCount(ongoingCount);

        // --- UPDATED ---
        setAssetValue(apiAssetValue); // Use the value from API root
        // --- END UPDATE ---

        setCurrentPage(1);

      } catch (err) {
        console.error('Projects fetch error:', err);
        if (err.response?.status === 401 || err.response?.data?.message?.includes('token')) {
          handleSessionExpired();
          return;
        }
        setError(`Error fetching projects: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [getValidToken, handleSessionExpired]);

  // Filters + sort
  useEffect(() => {
    let results = [...originalProjects];

    // Status filter: UI has "On-Going" or "Exited". API gives "on-going".
    if (statusFilter) {
      const uiStatus = statusFilter.toLowerCase();
      results = results.filter(p => p.status.toLowerCase() === uiStatus);
    }

    // Search
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      results = results.filter(p => {
        const fields = [
          p.title,
          p.key?.toString(),
          p.location, // This is compound name
          p.locationRaw, // This is original location (e.g. "Cairo")
          p.propertyType,
          p.currentStage,
          p.status,
          p.investmentType,
          p.area?.toString(),
          p.gardenArea?.toString(),
          p.totalContractValue?.toString(),
          p.assetEvaluation?.toString(),
        ];
        return fields.some(f => f && f.toLowerCase().includes(q));
      });
    }

    // Investment type
    if (investmentTypeFilter !== 'all') {
      // Compare against the "nice" display label
      results = results.filter(p => p.investmentType.toLowerCase() === investmentTypeFilter.toLowerCase());
    }

    // Property type
    if (propertyTypeFilter !== 'all') {
      // Compare against the "nice" display label
      results = results.filter(p => p.propertyType.toLowerCase() === propertyTypeFilter.toLowerCase());
    }

    // Sorting
    results.sort((a, b) => {
      switch (sortBy) {
        case 'Asset Value (High to Low)':
          // Use assetEvaluation for this sort
          return (b.assetEvaluation || 0) - (a.assetEvaluation || 0);
        case 'Asset Value (Low to High)':
          // Use assetEvaluation for this sort
          return (a.assetEvaluation || 0) - (b.assetEvaluation || 0);
        case 'Profit (High to Low)':
          return (b.profit || 0) - (a.profit || 0);
        case 'Profit (Low to High)':
          return (a.profit || 0) - (b.profit || 0);
        case 'Entry Date (Oldest First)':
          return (a.entryDate?.getTime() || 0) - (b.entryDate?.getTime() || 0);
        case 'Exit Date (Newest First)':
          return (b.exitDate?.getTime() || 0) - (a.exitDate?.getTime() || 0);
        case 'Exit Date (Oldest First)':
          return (a.exitDate?.getTime() || 0) - (b.exitDate?.getTime() || 0);
        case 'Entry Date (Newest First)':
        default:
          return (b.entryDate?.getTime() || 0) - (a.entryDate?.getTime() || 0);
      }
    });

    setFilteredProjects(results);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, investmentTypeFilter, propertyTypeFilter, sortBy, originalProjects]);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);
  const totalProjects = originalProjects.length; // Get total count for summary card

  // replace the existing handleProjectClick in InvestmentProjects
  const handleProjectClick = (project) => {
    try {
      sessionStorage.setItem('lastOpenedProject', JSON.stringify({ id: project.id, title: project.title }));
    } catch (e) {
      // ignore storage errors
    }
    navigate(`/projects/${project.id}`);
  };


  // UI

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <AnimatedBackground />
        <MuiSidebar />
        <MainContent>
          <ProjectsSkeleton />
        </MainContent>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex' }}>
        <AnimatedBackground />
        <MuiSidebar />
        <MainContent>
          <Typography color="error">{error}</Typography>
        </MainContent>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AnimatedBackground />
      <MuiSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        variant={isMobile ? 'temporary' : 'permanent'}
      />
      <MainContent>
        <Container maxWidth="xl">
          <Typography variant="h4" gutterBottom>
            Investment Projects
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Manage and monitor your investment portfolio
          </Typography>

          {/* Summary cards */}
          <StatsGrid>
            <StatCard accentcolor="#10B981">
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUpIcon />
                  <Typography variant="h6">Asset Value</Typography>
                </Box>
                <Typography variant="h4" sx={{ letterSpacing: '0.5px' }}>
                  {/* --- UPDATED --- */}
                  {formatNumber(assetValue)}
                  {/* --- END UPDATE --- */}
                </Typography>
              </Stack>
            </StatCard>

            <StatCard accentcolor="#3B82F6">
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AssignmentIcon />
                  <Typography variant="h6">Number of Projects</Typography>
                </Box>
                <Typography variant="h4" sx={{ letterSpacing: '0.5px' }}>
                  {/* This logic remains correct, but uses filteredProjects length for context */}
                  {statusFilter.toLowerCase() === 'on-going'
                    ? ongoingProjectsCount // Total ongoing
                    : statusFilter.toLowerCase() === 'exited'
                      ? totalProjects - ongoingProjectsCount // Total exited
                      : totalProjects}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography color="text.secondary">Total Active Projects</Typography>
                  <Typography color="text.primary">{ongoingProjectsCount}</Typography>
                </Box>
              </Stack>
            </StatCard>
          </StatsGrid>

          {/* Filters */}
          <FilterContainer>
            <SearchInput
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <StyledSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)} displayEmpty>
              <MenuItem value="Entry Date (Newest First)">Entry Date (Newest First)</MenuItem>
              <MenuItem value="Entry Date (Oldest First)">Entry Date (Oldest First)</MenuItem>
              <MenuItem value="Asset Value (High to Low)">Asset Value (High to Low)</MenuItem>
              <MenuItem value="Asset Value (Low to High)">Asset Value (Low to High)</MenuItem>
              <MenuItem value="Profit (High to Low)">Profit (High to Low)</MenuItem>
              <MenuItem value="Profit (Low to High)">Profit (Low to High)</MenuItem>
              <MenuItem value="Exit Date (Newest First)">Exit Date (Newest First)</MenuItem>
              <MenuItem value="Exit Date (Oldest First)">Exit Date (Oldest First)</MenuItem>
            </StyledSelect>

            <StyledSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="On-Going">On-Going</MenuItem>
              <MenuItem value="Exited">Exited</MenuItem>
            </StyledSelect>

            <StyledSelect
              value={investmentTypeFilter}
              onChange={(e) => setInvestmentTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="Buy To Hold">Buy To Hold</MenuItem>
              <MenuItem value="Rent">Rent</MenuItem>
              <MenuItem value="Capital Gain">Capital Gain</MenuItem>
            </StyledSelect>

            <StyledSelect
              value={propertyTypeFilter}
              onChange={(e) => setPropertyTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="Villa">Villa</MenuItem>
              <MenuItem value="Apartment">Apartment</MenuItem>
              <MenuItem value="Duplex">Duplex</MenuItem>
              <MenuItem value="Chalet">Chalet</MenuItem>
              <MenuItem value="Property">Property</MenuItem>
            </StyledSelect>
          </FilterContainer>

          {/* Projects */}
          <ProjectsGrid>
            {currentProjects.map((project) => (
              <Grid
                item
                key={project.id}
                onClick={() => handleProjectClick(project)}
              >
                <ProjectCard>
                  {/* --- UPDATED --- */}
                  <Box sx={{ position: 'relative', width: '100%', height: 200, backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
                    {project.image ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          // Optional: Handle image load error, e.g., show placeholder
                          e.target.style.display = 'none';
                          // You might want a sibling element to show a placeholder text
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.background.paper, 0.5),
                          backdropFilter: 'blur(10px)',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No image available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {/* --- END UPDATE --- */}

                  <ProjectContent>
                    <div className="project-header">
                      {/* --- UPDATED --- */}
                      <Typography className="project-id">#{project.key || '—'}</Typography>
                      {/* --- END UPDATE --- */}
                      <Chip
                        label={project.status}
                        size="small"
                        color={project.status.toLowerCase() === 'on-going' ? 'success' : 'info'}
                      />
                    </div>

                    <Typography className="project-title" variant="h6" gutterBottom>
                      {project.title}
                    </Typography>

                    <div className="project-info-highlight">
                      <span className="project-location">{project.location}</span>
                      <span className="project-property">{project.propertyType}</span>
                      <span className="project-stage">{project.currentStage}</span>
                    </div>

                    <div className="info-row">
                      <div>
                        <div className="info-label">Contract Value</div>
                        <div className="info-value">{formatNumber(project.investedAmount)}</div>
                      </div>
                      <div>
                        <div className="info-label">Asset Evaluation</div>
                        <div className="info-value">{formatNumber(project.assetEvaluation)}</div>
                      </div>
                    </div>

                    <div className="info-row">
                      <div>
                        <div className="info-label">Reservation Date</div>
                        <div className="info-value">{formatDate(project.entryDate)}</div>
                      </div>
                      <div>
                        <div className="info-label">Contract Date</div>
                        <div className="info-value">{formatDate(project.contractDate)}</div>
                      </div>
                    </div>

                    <div className="project-targets">
                      {project.investmentType && (
                        <span className="target-label">{project.investmentType}</span>
                      )}
                      {project.propertyType && (
                        <span className="target-label">{project.propertyType}</span>
                      )}
                    </div>
                  </ProjectContent>
                </ProjectCard>
              </Grid>
            ))}
          </ProjectsGrid>

          {/* Pagination */}
          {filteredProjects.length > projectsPerPage && (
            <PaginationContainer>
              <div className="pagination">
                <button
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                  className="page-nav"
                >
                  First
                </button>
                <button
                  onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                  disabled={currentPage === 1}
                  className="page-nav"
                >
                  «
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(number => (
                    number <= 3 ||
                    number > totalPages - 3 ||
                    Math.abs(currentPage - number) <= 1
                  ))
                  .map((number, index, array) => {
                    if (index > 0 && array[index - 1] !== number - 1) {
                      return (
                        <React.Fragment key={`ellipsis-${number}`}>
                          <span style={{ margin: '0 8px' }}>...</span>
                          <button
                            onClick={() => paginate(number)}
                            className={`page-number ${currentPage === number ? 'active' : ''}`}
                          >
                            {number}
                          </button>
                        </React.Fragment>
                      );
                    }
                    return (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`page-number ${currentPage === number ? 'active' : ''}`}
                      >
                        {number}
                      </button>
                    );
                  })}

                <button
                  onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                  disabled={currentPage === totalPages}
                  className="page-nav"
                >
                  »
                </button>
                <button
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                  className="page-nav"
                >
                  Last
                </button>
              </div>
              <div className="page-info">Page {currentPage} of {totalPages}</div>
            </PaginationContainer>
          )}
        </Container>
      </MainContent>

      <SessionExpiredPopup
        open={isSessionExpired}
        onClose={closeSessionExpired}
      />
    </Box>
  );
};

export default InvestmentProjects;
